import { useQueryWithAuthError } from "@ssf/auth-components";
import type { QueryOptions } from "@ssf/components";
import {
  getTournamentGroup,
  getTournamentSchoolResults,
  OrgType,
} from "@ssf/memdb-api";
import { zonedTimeToUtc } from "date-fns-tz";

const getTime = (date: string) =>
  zonedTimeToUtc(date, "Europe/Stockholm").getTime();

const getTournamentGroupWithSchema = (groupId: number) =>
  getTournamentGroup({
    groupId,
    params: {
      schema: {
        start: 1,
        end: 1,
        arenaStart: 1,
        arenaEnd: 1,
        pairingsystem: 1,
        contenders: [
          { partid: 1, teamnumber: 1, atvenue: 1, name: 1, type: 1 },
        ],
      },
    },
  }).then(
    ({
      start,
      arenaStart = "00:00",
      end,
      arenaEnd = "23:59",
      contenders,
      ...rest
    }) => {
      const playerContenders: typeof contenders = [];
      const teamContenders: typeof contenders = [];
      contenders?.forEach((contender) => {
        if (!contender.type) {
          playerContenders.push(contender);
        }
        if (contender.type === OrgType.Team) {
          teamContenders.push(contender);
        }
      });
      return {
        start: getTime(`${start} ${arenaStart}`),
        end: getTime(`${end} ${arenaEnd}`),
        playerContenders: playerContenders.sort((a, b) =>
          a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1,
        ),
        teamContenders: teamContenders.sort((a, b) =>
          a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1,
        ),
        contenders,
        ...rest,
      };
    },
  );

export const useMemdbTournament = (
  groupId: number,
  options?: QueryOptions<typeof getTournamentGroupWithSchema>,
) => {
  const retry = options?.retry === true;
  return useQueryWithAuthError(
    ["useTournamentRoomGroup", groupId],
    () => getTournamentGroupWithSchema(groupId),
    retry ? { ...options, retry: true, retryDelay: 5000 } : options,
    retry ? () => true : undefined,
  );
};

const getTournamentGroupResultsWithSchema = (groupId: number) =>
  getTournamentGroup({
    groupId,
    params: {
      schema: {
        results: [{ partid: 1, teamnumber: 1, points: 1, place: 1 }],
      },
    },
  });
export const useMemdbTournamentResults = (
  groupId: number,
  options?: QueryOptions<typeof getTournamentGroupResultsWithSchema>,
) => {
  const retry = options?.retry === true;
  return useQueryWithAuthError(
    ["useMemdbTournamentResults", groupId],
    () => getTournamentGroupResultsWithSchema(groupId),
    retry ? { ...options, retry: true, retryDelay: 5000 } : options,
    retry ? () => true : undefined,
  );
};

export const useMemdbTournamentSchoolResults = (
  groupId: number,
  options?: QueryOptions<typeof getTournamentSchoolResults>,
) =>
  useQueryWithAuthError(
    ["useMemdbTournamentSchoolResults", groupId],
    () => getTournamentSchoolResults(groupId),
    { retry: 2, staleTime: 30 * 1000, ...options },
    () => true,
  );
